import { debounce } from 'es-toolkit';
import { notyf } from '@modules';
import { pubsub } from '@services';

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.buttons = this.querySelectorAll('button');
    this.min = this.input.min;
    this.max = this.input.max || '500'; // Enforce global maximum of 500

    this.buttons.forEach((button) => {
      button.addEventListener('click', (event) => this.handleStep(event.currentTarget.name));
    });

    this.input.addEventListener('change', () => {
      this.constrainAndUpdateInput(this.input.value);
      this.publishEvents();
    });

    this.intent = this.dataset.intent;
  }

  /**
   * Handle the step up or step down of the quantity
   * @param {string} type - The type of step to be taken
   */
  handleStep(type) {
    const value = parseInt(this.input.value);
    const step = parseInt(this.input.step) || 1;
    const newValue = type === 'minus' ? Math.max(value - step, 0) : value + step;

    // Intercept before debounce: show remove dialog immediately when stepping to 0
    if (newValue === 0 && this.intent === 'cart:change') {
      this.#showRemoveDialog();
      return;
    }

    const updatedValue = this.constrainAndUpdateInput(newValue);

    if (parseInt(updatedValue) !== parseInt(value)) {
      this.publishEvents();
    }
  }

  /**
   * Show the remove confirmation dialog instead of directly removing the item.
   * Reuses the shared dialog from line-items.
   */
  #showRemoveDialog() {
    const lineItem = this.closest('line-item');
    if (!lineItem) return false;

    const removeButton = lineItem.querySelector('[data-line-item-remove]');
    if (!removeButton) return false;

    const productNameElement = document.getElementById('remove-item-product-name');
    const confirmRemoveButton = document.getElementById('confirm-remove-button');
    const dialog = document.getElementById('remove-item-dialog');

    if (!productNameElement || !confirmRemoveButton || !dialog) return false;

    productNameElement.textContent = removeButton.dataset.productTitle;
    confirmRemoveButton.dataset.lineItemIndex = lineItem.dataset.lineItemIndex;
    dialog.showModal();

    // Reset quantity back to 1
    this.input.value = 1;

    return true;
  }

  publishEvents = debounce(
    () => {
      switch (this.intent) {
        case 'cart:change':
          if (parseInt(this.input.value) === 0) {
            this.#showRemoveDialog();
            return;
          }
          pubsub.publish('cart:change', {
            line: this.dataset.itemIndex,
            quantity: this.input.value,
          });
          break;
        case 'quantity:change':
          pubsub.publish('quantity:change', {
            quantity: this.input.value,
          });
          break;
        default:
          break;
      }
    },
    this.dataset.debounce === 'true' ? 300 : 0
  );

  /**
   * Constrain and update the input.
   * @param {number} newValue - The new value.
   * @returns {number} The constrained value.
   */
  constrainAndUpdateInput(newValue) {
    this.input.value = newValue;

    if (newValue >= this.max && this.max !== '') {
      notyf.error(
        window.toast_translations.product.quantity_error_max.replace('{{ max }}', this.max)
      );
      this.input.value = this.max;
    } else if (newValue <= this.min) this.input.value = this.min;

    return this.input.value;
  }
}

customElements.define('quantity-input', QuantityInput);
